import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShoppingCart, CheckCircle2, Package, Share2 } from 'lucide-react';
import { trackProductView, trackAddToCart, trackShare } from '@/lib/analytics';
import { cartUtils } from '@/data/translatedData';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { useExchangeRate } from '@/hooks/useExchangeRate';
import { tx } from '@/translations/translations';
import { displaySize } from '@/utils/displaySize';
import { deriveStockState, isSkuOutOfStock } from '@/lib/productStock';
import { ProductImageCarousel } from './product/ProductImageCarousel';
import { ProductTabs } from './product/ProductTabs';
import { RelatedProducts } from './product/RelatedProducts';
import {
  ColorSelector, CottonTypeSelector, ThreadSelector, TraditionSelector,
  CustomSelections, SizeSelector, traditionDefaultLabel,
} from './product/variantSelectors';

const ProductModal = ({ product, onClose, allProducts = [], onSelectProduct }) => {
  const { language } = useLanguage();
  const { ilsRate } = useExchangeRate();
  // Track product view analytics on mount / when product changes
  useEffect(() => {
    if (product?.id) trackProductView(product.id);
  }, [product?.id]);
  const [selectedColor, setSelectedColor] = useState(
    product.colors
      ? (product.colors.find(c => !c.outOfStock && !product.outOfStockColors?.includes(c.value)) || product.colors[0])
      : null
  );
  const [selectedSize, setSelectedSize] = useState(
    product.outOfStockSizes
      ? (product.sizes.find(s => !product.outOfStockSizes.includes(s)) || product.sizes[0])
      : product.sizes[0]
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedTradition, setSelectedTradition] = useState('ashkenazi');
  const [selectedHilos, setSelectedHilos] = useState('avodat_yad_grueso');
  const [selectedCottonType, setSelectedCottonType] = useState('cotton_smooth');
  const [showingTexture, setShowingTexture] = useState(false);
  const [carouselOverride, setCarouselOverride] = useState(false);
  const [activeTab, setActiveTab] = useState('features');

  const isWool = product.material === 'lana';
  const hasPricingRules = !!product.pricingRules;

  // Keep selections in sync when the product prop changes: either a different
  // product (related products) or the same product re-transformed on language switch.
  const prevProductRef = React.useRef(product);
  useEffect(() => {
    const prev = prevProductRef.current;
    if (prev === product) return;
    prevProductRef.current = product;
    if (prev.id !== product.id) {
      setSelectedColor(product.colors
        ? (product.colors.find(c => !c.outOfStock && !product.outOfStockColors?.includes(c.value)) || product.colors[0])
        : null);
      setSelectedSize(product.outOfStockSizes
        ? (product.sizes.find(s => !product.outOfStockSizes.includes(s)) || product.sizes[0])
        : product.sizes[0]);
      setQuantity(1);
      return;
    }
    setSelectedColor(c => c ? ((product.colors || []).find(x => x.value === c.value) || c) : c);
    setSelectedSize(s => {
      const idx = (prev.sizes || []).indexOf(s);
      return idx >= 0 && product.sizes?.[idx] ? product.sizes[idx] : s;
    });
  }, [product]);

  // Dynamic SEO: per-product title + description while the modal is open
  useEffect(() => {
    if (!product) return undefined;
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content');
    document.title = `${product.name} | Mitzva Fit`;
    if (metaDesc && product.description) metaDesc.setAttribute('content', product.description.slice(0, 160));
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute('content', prevDesc);
    };
  }, [product]);

  // Kids size: contains any digit (2-4, 6-8, 10-12…), or Niño/Boys prefix
  const isKidsSize = /\d/.test(selectedSize) || selectedSize.startsWith('Niño') || selectedSize.includes('Boys');

  // Available thread options based on size and pricing rules (admin-hidden filtered out)
  const availableThreads = React.useMemo(() => {
    const threads = hasPricingRules
      ? (isKidsSize ? product.pricingRules.kidsThreads : product.pricingRules.adultThreads)
      : ['avodat_yad_grueso', 'avodat_yad_delgado', 'sin_hilos'];
    const hidden = product.hiddenThreads || [];
    return threads.filter(t => !hidden.includes(t));
  }, [hasPricingRules, isKidsSize, product.pricingRules, product.hiddenThreads]);

  // Derive OOS state from stock_qty for products that use stock-driven matrices
  const stockDerived = React.useMemo(
    () => deriveStockState(product.stockQty, product.sizes),
    [product.stockQty, product.sizes]
  );

  const outOfStockThreads = React.useMemo(
    () => product.outOfStockThreads || [],
    [product.outOfStockThreads]
  );
  const outOfStockTraditions = product.outOfStockTraditions || [];
  const effectiveOutOfStockColors = product.outOfStockColors || [];
  const effectiveOutOfStockCottonTypes = product.outOfStockCottonTypes || [];
  const effectiveOutOfStockSizes = stockDerived
    ? stockDerived.oosSizes
    : (product.outOfStockSizes || null);
  const forceShowOptions = true;

  // Per-size stock check for the selected size
  const currentSkuOutOfStock = React.useMemo(
    () => isSkuOutOfStock(product.stockQty, selectedSize),
    [product.stockQty, selectedSize]
  );
  // Custom selections (created from the Admin edit modal)
  const visibleCustomSelections = (product.customSelections || []).filter(cs => cs.state !== 'hidden');
  const [customChoices, setCustomChoices] = React.useState({});
  React.useEffect(() => {
    const init = {};
    (product.customSelections || []).filter(cs => cs.state !== 'hidden').forEach(cs => {
      const first = (cs.options || []).find(o => (o.state || 'visible') === 'visible');
      if (first) init[cs.id] = first.key;
    });
    setCustomChoices(init);
  }, [product.id, product.customSelections]);

  // Products with single "Incluidos" hilo option
  const hilosIncluded = [2, 3, 7, 8, 9, 11, 12, 13, 14, 15, 16].includes(product.id);
  // Products that keep "No Amarrados" tradition: 1, 4, 5, 7. All others hide it.
  const hideNoAmarrados = ![1, 4, 5, 7].includes(product.id);

  // Auto-correct thread selection when size changes or thread goes OOS
  React.useEffect(() => {
    if (!availableThreads.includes(selectedHilos) || outOfStockThreads.includes(selectedHilos)) {
      const firstAvailable = availableThreads.find(t => !outOfStockThreads.includes(t));
      if (firstAvailable) setSelectedHilos(firstAvailable);
    }
  }, [selectedSize, availableThreads, outOfStockThreads, selectedHilos]);

  // Auto-correct selected cotton type if it goes OOS or gets hidden
  React.useEffect(() => {
    if (!product.hasCottonType) return;
    const oos = product.outOfStockCottonTypes || [];
    const hid = product.hiddenCottonTypes || [];
    if (oos.includes(selectedCottonType) || hid.includes(selectedCottonType)) {
      const fallback = ['cotton_smooth', 'cotton_edge'].find(c => !oos.includes(c) && !hid.includes(c));
      if (fallback) setSelectedCottonType(fallback);
    }
  }, [product.outOfStockCottonTypes, product.hiddenCottonTypes, product.hasCottonType, selectedCottonType]);

  // Auto-correct selected tradition if it's hidden by admin
  React.useEffect(() => {
    const hid = product.hiddenTraditions || [];
    if (hid.includes(selectedTradition)) {
      const fallback = ['ashkenazi', 'sefaradi', 'no_amarrados'].find(t => !hid.includes(t));
      if (fallback) setSelectedTradition(fallback);
    }
  }, [product.hiddenTraditions, selectedTradition]);

  // Auto-correct selected color if it's hidden by admin
  React.useEffect(() => {
    const hid = product.hiddenColors || [];
    if (selectedColor && hid.includes(selectedColor.value)) {
      const fallback = (product.colors || []).find(c => !hid.includes(c.value));
      if (fallback) setSelectedColor(fallback);
    }
  }, [product.hiddenColors, selectedColor, product.colors]);

  // Auto-correct selected hilo if it becomes unavailable (e.g. admin hid it)
  React.useEffect(() => {
    if (availableThreads.length === 0) return;
    if (!availableThreads.includes(selectedHilos)) {
      const firstAvailable = availableThreads.find(t => !outOfStockThreads.includes(t)) || availableThreads[0];
      setSelectedHilos(firstAvailable);
    }
  }, [product.hiddenThreads, availableThreads, outOfStockThreads, selectedHilos]);

  // Get dynamic price
  const sinHilosDiscount = language !== 'en' ? 50 : Math.round((50 / 17.24) * 100) / 100;
  const noAmarradosDiscount = language !== 'en' ? 30 : Math.round((30 / 17.24) * 100) / 100;

  const getDisplayPrice = () => {
    let base;
    if (hasPricingRules) {
      if (isKidsSize) {
        if (selectedHilos === 'sin_hilos' && product.pricingRules.kidsPrices?.sin_hilos) {
          base = product.pricingRules.kidsPrices.sin_hilos;
        } else {
          base = product.pricingRules.kidsPrice;
        }
      } else {
        base = product.pricingRules.adultPrices[selectedHilos] || product.price;
      }
    } else if (product.kidsPrice !== undefined) {
      // Admin set separate adult/kids prices in Inventory
      base = isKidsSize ? product.kidsPrice : (product.adultPrice ?? product.price);
      if (selectedHilos === 'sin_hilos') base = base - sinHilosDiscount;
    } else if (isKidsSize && product.kidsDiscount) {
      base = product.price - product.kidsDiscount;
      if (selectedHilos === 'sin_hilos') base = base - sinHilosDiscount;
    } else {
      base = product.adultPrice ?? product.price;
      if (selectedHilos === 'sin_hilos') base = base - sinHilosDiscount;
    }
    if (selectedTradition === 'no_amarrados' && selectedHilos !== 'sin_hilos') {
      base = base - noAmarradosDiscount;
    }
    return base;
  };

  const displayPrice = getDisplayPrice();

  // Get the current image based on selected color
  const currentImage = selectedColor?.image || product.image;

  // Skip product-level gallery for TZI-SHIRT products (3, 8, 9), Lana Tank (2), Algodón Tank V (14), Lana Tank V (15)
  const skipProductLevelGallery = [2, 3, 8, 9, 14, 15].includes(product.id);
  const galleryImages = React.useMemo(() => {
    const imgs = [];
    if (currentImage) imgs.push(currentImage);
    // If color has a kid-specific gallery, swap based on selected size
    const useKidGallery = isKidsSize && selectedColor?.kidGallery && selectedColor.kidGallery.length > 0;
    const colorGallery = useKidGallery ? selectedColor.kidGallery : (selectedColor?.gallery || []);
    colorGallery.forEach(g => { if (!imgs.includes(g)) imgs.push(g); });
    if (!skipProductLevelGallery) {
      if (product.image && !imgs.includes(product.image)) imgs.push(product.image);
      const productGallery = product.gallery || [];
      productGallery.forEach(g => { if (!imgs.includes(g)) imgs.push(g); });
    }
    return imgs;
  }, [currentImage, selectedColor, product.gallery, product.image, isKidsSize, skipProductLevelGallery]);

  const [galleryIdx, setGalleryIdx] = useState(0);
  React.useEffect(() => { setGalleryIdx(0); setCarouselOverride(false); }, [selectedColor?.value, selectedColor?.name, product.product_id, isKidsSize, selectedSize]);
  const heroImage = galleryImages[galleryIdx] || currentImage;

  // Carousel navigation: keyboard arrows + swipe gestures
  const isTextureView = product.hasCottonType && showingTexture && !carouselOverride;
  const isSizeImgView = !carouselOverride && product.sizeImages && product.sizeImages[selectedSize];
  const carouselLen = galleryImages.length;
  const goToImage = React.useCallback((targetIdx) => {
    if (carouselLen === 0) return;
    const wrapped = ((targetIdx % carouselLen) + carouselLen) % carouselLen;
    setGalleryIdx(wrapped);
    if (product.hasCottonType) setShowingTexture(false);
    setCarouselOverride(true);
  }, [carouselLen, product.hasCottonType]);
  const handlePrev = React.useCallback(() => goToImage(galleryIdx - 1), [galleryIdx, goToImage]);
  const handleNext = React.useCallback(() => goToImage(galleryIdx + 1), [galleryIdx, goToImage]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlePrev, handleNext]);

  const touchStartX = React.useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) handleNext(); else handlePrev();
    }
    touchStartX.current = null;
  };

  const handleSelectThumb = (idx) => {
    setGalleryIdx(idx);
    if (product.hasCottonType) setShowingTexture(false);
    setCarouselOverride(true);
  };

  // Compute related products (random, excluding current). Re-rolls when current product changes.
  const relatedProducts = React.useMemo(() => {
    const pool = (allProducts || []).filter(p => p.id !== product.id);
    const arr = [...pool];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, 4);
  }, [product.id, allProducts]);

  // Scroll modal content to top when switching to a related product
  const dialogScrollRef = React.useRef(null);
  React.useEffect(() => {
    if (dialogScrollRef.current) dialogScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, [product.id]);

  const handleShare = () => {
    const url = `${window.location.origin}/api/p/${product.id}`;
    const shareText = `${product.name}\n${url}`;
    trackShare(product.id, 'modal');
    if (navigator.share) {
      navigator.share({ title: `${product.name} | Mitzva Fit`, text: product.name, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
    }
    navigator.clipboard?.writeText(url).then(() => {
      toast.success(tx(language, 'Enlace copiado', 'Link copied', 'הקישור הועתק'));
    }).catch(() => {});
  };

  const handleAddToCart = () => {
    const customSelections = {};
    visibleCustomSelections.forEach(cs => {
      const opt = (cs.options || []).find(o => o.key === customChoices[cs.id]);
      if (opt) customSelections[cs.name?.es || cs.name || 'Selección'] = opt.label;
    });
    const productWithOptions = {
      ...product,
      price: displayPrice,
      selectedColor: selectedColor,
      selectedTradition,
      selectedHilos,
      selectedCottonType,
      ...(Object.keys(customSelections).length > 0 ? { customSelections } : {})
    };
    cartUtils.addToCart(productWithOptions, selectedSize, quantity);
    window.dispatchEvent(new Event('cartUpdated'));
    trackAddToCart(product.id);
    // Haptic feedback (mobile)
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([15, 30, 15]);
    }
    // Trigger global "cart pulse" visual
    window.dispatchEvent(new CustomEvent('cartItemAdded', { detail: { name: product.name } }));

    const colorText = selectedColor ? ` - ${selectedColor.name}` : '';
    const traditionLabel = traditionDefaultLabel(selectedTradition, language);
    const hilosLabels = {
      'avodat_yad_grueso': tx(language, 'A Mano Gruesos', 'Handmade Thick', 'עבודת יד עבים'),
      'avodat_yad_delgado': tx(language, 'A Mano Delgados', 'Handmade Thin', 'עבודת יד דקים'),
      'mechona_delgado': tx(language, 'A Máquina', 'Machine', 'מכונה'),
      'sin_hilos': tx(language, 'Sin Hilos', 'No Threads', 'ללא חוטים')
    };
    const hilosLabel = hilosLabels[selectedHilos];
    toast.success(`${product.name} ${tx(language, 'agregado al carrito', 'added to cart', 'נוסף לסל')}`, {
      description: `${tx(language, 'Talla', 'Size', 'מידה')}: ${displaySize(selectedSize, language)}${colorText} - ${traditionLabel} - ${hilosLabel}`
    });
    onClose();
  };

  const selectionSoldOut = !product.inStock || selectedColor?.outOfStock ||
    effectiveOutOfStockColors.includes(selectedColor?.value) ||
    (effectiveOutOfStockSizes || []).includes(selectedSize) || currentSkuOutOfStock;
  let addToCartLabel = tx(language, 'Agregar al Carrito', 'Add to Cart', 'הוסף לסל');
  if (product.comingSoon) {
    addToCartLabel = tx(language, 'Próximamente', 'Coming Soon', 'בקרוב');
  } else if (selectionSoldOut) {
    addToCartLabel = tx(language, 'Agotado', 'Sold Out', 'אזל מהמלאי');
  }

  const openSizeGuide = () => {
    setActiveTab('sizes');
    setTimeout(() => {
      const el = document.querySelector('[data-testid="size-guide-tab-content"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent ref={dialogScrollRef} className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 w-[96vw] sm:w-full rounded-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <ProductImageCarousel
            product={product}
            galleryImages={galleryImages}
            galleryIdx={galleryIdx}
            heroImage={heroImage}
            isTextureView={isTextureView}
            isSizeImgView={isSizeImgView}
            selectedCottonType={selectedCottonType}
            selectedSize={selectedSize}
            showingTexture={showingTexture}
            carouselOverride={carouselOverride}
            onPrev={handlePrev}
            onNext={handleNext}
            onSelectThumb={handleSelectThumb}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          />

          {/* Right — Details */}
          <div className="p-3 sm:p-6 md:p-8">
            <DialogHeader className="space-y-1 p-0">
              <div className="flex items-start justify-between gap-2">
                <DialogTitle className="text-base sm:text-xl md:text-2xl font-bold text-slate-900 leading-tight">{product.name}</DialogTitle>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-50 ring-1 ring-emerald-200 flex items-center justify-center hover:bg-emerald-100 transition-colors"
                  title={tx(language, 'Compartir producto', 'Share product', 'שתף מוצר')}
                  data-testid="modal-share-btn"
                >
                  <Share2 className="w-4 h-4 text-emerald-600" />
                </button>
              </div>
              <DialogDescription className="text-xs sm:text-sm text-slate-500">{product.description}</DialogDescription>
            </DialogHeader>

            {/* Price + Stock */}
            <div className="mt-3 sm:mt-5 flex items-center gap-2 sm:gap-3 flex-wrap">
              <span className="text-2xl sm:text-3xl font-black text-slate-900" data-testid="modal-price">
                ${displayPrice.toFixed(0)}
                <span className="text-xs sm:text-sm font-normal text-slate-400 ml-1">{product.currency}</span>
              </span>
              {language === 'he' && ilsRate && (
                <span className="text-sm font-semibold text-slate-400" data-testid="modal-ils-price">
                  ≈ ₪{Math.round(displayPrice / ilsRate).toLocaleString('he-IL')}
                </span>
              )}
              {product.inStock ? (
                <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-full border border-green-200">
                  <CheckCircle2 className="h-3 w-3" />
                  {tx(language, 'En Stock', 'In Stock', 'במלאי')}
                </span>
              ) : (
                <span className="bg-red-50 text-red-700 text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-full border border-red-200">
                  {tx(language, 'Agotado', 'Sold Out', 'אזל מהמלאי')}
                </span>
              )}
              {product.noTzitzit && (
                <span
                  data-testid="modal-pack-badge"
                  className="inline-flex items-center gap-1 bg-slate-900 text-white text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-full shadow-sm"
                >
                  <Package className="h-3 w-3" />
                  {tx(language, 'Incluye 3 piezas', 'Includes 3 pieces', 'כולל 3 יחידות')}
                </span>
              )}
              {(product.badges || []).map((b) => (
                <span key={b} data-testid="modal-custom-badge" className="inline-flex items-center bg-slate-900 text-white text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                  {b}
                </span>
              ))}
            </div>

            <ColorSelector
              product={product}
              language={language}
              selectedColor={selectedColor}
              onSelect={setSelectedColor}
              outOfStockColors={effectiveOutOfStockColors}
              forceShowOptions={forceShowOptions}
            />

            <CottonTypeSelector
              product={product}
              language={language}
              selectedCottonType={selectedCottonType}
              onSelect={(key) => { setSelectedCottonType(key); setShowingTexture(true); }}
              outOfStockCottonTypes={effectiveOutOfStockCottonTypes}
            />

            <ThreadSelector
              product={product}
              language={language}
              isKidsSize={isKidsSize}
              isWool={isWool}
              hilosIncluded={hilosIncluded}
              availableThreads={availableThreads}
              outOfStockThreads={outOfStockThreads}
              selectedHilos={selectedHilos}
              setSelectedHilos={setSelectedHilos}
              forceShowOptions={forceShowOptions}
              onClose={onClose}
            />

            <TraditionSelector
              product={product}
              language={language}
              hideNoAmarrados={hideNoAmarrados}
              selectedHilos={selectedHilos}
              selectedTradition={selectedTradition}
              setSelectedTradition={setSelectedTradition}
              outOfStockTraditions={outOfStockTraditions}
              forceShowOptions={forceShowOptions}
              onClose={onClose}
            />

            <CustomSelections
              visibleCustomSelections={visibleCustomSelections}
              customChoices={customChoices}
              setCustomChoices={setCustomChoices}
            />

            <SizeSelector
              product={product}
              language={language}
              selectedSize={selectedSize}
              setSelectedSize={setSelectedSize}
              outOfStockSizes={effectiveOutOfStockSizes}
              forceShowOptions={forceShowOptions}
              onOpenSizeGuide={openSizeGuide}
            />

            {/* Quantity + Add to Cart */}
            <div className="flex items-center gap-3 sm:gap-4 pt-2">
              <div className="flex items-center border-2 border-slate-200 rounded-xl overflow-hidden flex-shrink-0">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-2.5 py-1.5 sm:px-3 sm:py-2 text-slate-600 hover:bg-slate-50 font-bold">-</button>
                <span className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm font-semibold min-w-[2.5rem] sm:min-w-[3rem] text-center">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="px-2.5 py-1.5 sm:px-3 sm:py-2 text-slate-600 hover:bg-slate-50 font-bold">+</button>
              </div>
              <Button
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3.5 sm:py-5 rounded-xl text-xs sm:text-sm"
                onClick={handleAddToCart}
                disabled={product.comingSoon || selectionSoldOut}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                {addToCartLabel}
              </Button>
            </div>
          </div>
        </div>

        <ProductTabs
          product={product}
          language={language}
          isKidsSize={isKidsSize}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        <RelatedProducts
          relatedProducts={relatedProducts}
          language={language}
          onSelectProduct={onSelectProduct}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
